// Country to Currency mapping
const COUNTRY_CURRENCY_MAP = {
  US: 'USD',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'NZD',
  BB: 'BBD',
  KY: 'KYD',
  GB: 'GBP',
}

export function getCurrencyByCountry(countryCode) {
  return COUNTRY_CURRENCY_MAP[countryCode] || 'USD'
}

const KEAP_WRAPPER_CODES = new Set([
  'addon-5-keap-addons',
  'addon-5-keap-addons-annual',
  'addon-5-keap-addons-legacy-m2m',
  'addon-5-keap-addons-legacy-annual',
])

const ensureKeapAddonsWrapper = addOnCode => {
  const apps = Array.isArray(addOnCode) ? [...addOnCode] : []

  const isWrapper = code => KEAP_WRAPPER_CODES.has(String(code || ''))
  const isKeapChild = code => {
    const c = String(code || '')
    return c.startsWith('addon-5-keap-') && !isWrapper(c)
  }

  const wrapperItem = apps.find(app => isWrapper(app.code))
  const keapAddonWrapperCode =
    wrapperItem?.code ||
    apps.find(app => isWrapper(app.parentProduct))?.parentProduct

  const hasKeapRecurringChildAddon = apps.some(app => isKeapChild(app.code))

  if (!hasKeapRecurringChildAddon || !keapAddonWrapperCode) {
    return apps
  }

  const childAddons = apps.filter(app => isKeapChild(app.code))
  const otherApps = apps.filter(
    app => !isKeapChild(app.code) && !isWrapper(app.code),
  )

  return [
    ...otherApps,
    {
      code: keapAddonWrapperCode,
      quantity: 1,
      addons: childAddons,
    },
  ]
}

export function restructureCart(cart) {
  if (!cart) return cart

  const updatedApps = []
  cart.apps?.forEach(app => {
    if (app.groupKey === 'growthPackage') {
      // find appmarket-5-thryv-m2m addon
      const appMarket = app.addons?.find(
        a => a.code === 'appmarket-5-thryv-m2m',
      )

      if (appMarket) {
        const otherAddons = app.addons.filter(
          a => a.code !== 'appmarket-5-thryv-m2m',
        )

        const parentApp = {
          ...appMarket,
          addons: [
            ...otherAddons,
            {
              id: app.id,
              code: app.code,
              name: app.name,
              currencies: app.currencies,
              entitlements: app.entitlements,
              requires:
                app?.requires?.length > 0
                  ? app.requires
                  : app?.requiresCenter || [],
              groupKey: app.groupKey,
              section: app.section,
              selectedEntitlements: app.selectedEntitlements,
              addons: [],
            },
          ],
        }

        updatedApps.push(parentApp)
      } else {
        updatedApps.push(app)
      }
    } else {
      updatedApps.push(app)
    }
  })

  return {...cart, apps: updatedApps}
}

export const formatCartToPayload = (
  cartObj,
  defaultObFeeCoupon,
  PlanIncentive,
  promotions,
  selectedCountry = 'US',
) => {
  const formatItem = (item, _type) => {
    return {
      code: item.code,
      quantity: item.quantity || 1,
      ...(item.isAlreadyAdded != null
        ? {isAlreadyAdded: item.isAlreadyAdded}
        : {}),
      addons: (item.addons || []).map(addon => ({
        code: addon.code,
        quantity: addon.quantity || 1,
        ...(addon.isAlreadyAdded != null
          ? {isAlreadyAdded: addon.isAlreadyAdded}
          : {}),
      })),
    }
  }

  const addOnCode = ensureKeapAddonsWrapper(
    (cartObj.apps || []).map(app => formatItem(app, 'apps')),
  )

  return {
    planCode: (cartObj.centers || []).map(center =>
      formatItem(center, 'centers'),
    ),
    addOnCode,
    oneTimeFees: (cartObj.onetimeFees || []).map(fee =>
      formatItem(fee, 'onetimeFees'),
    ),
    couponCode: [
      ...PlanIncentive.map(incentive => incentive.code),
      ...promotions.map(coupon => coupon.code),
    ],
    defaultIncentive: defaultObFeeCoupon.map(coupon => coupon.code),
  }
}

export const isCartEmpty = cart => {
  if (!cart) return true
  const {apps = [], centers = [], onetimeFees = []} = cart
  let autoAddedItem = ['cc_basicpls-5-thryv-m2m', 'appmarket-5-thryv-m2m']

  const filteredApps = apps.filter(app => !autoAddedItem.includes(app.code))
  const filteredCenters = centers.filter(
    center => !autoAddedItem.includes(center.code),
  )
  const filteredOnetimeFees = onetimeFees.filter(
    fee => !autoAddedItem.includes(fee.code),
  )

  if (
    filteredApps.length === 0 &&
    filteredCenters.length === 0 &&
    filteredOnetimeFees.length === 0
  ) {
    return true
  }

  return apps.length === 0 && centers.length === 0 && onetimeFees.length === 0
}

export const getKeapAdditionalContactsByCountry = (
  selectedCountry = 'US',
  billingType = 'Monthly',
) => {
  const isAnnual = billingType?.includes('Annual')
  let additionalContactOptions = []
  if (selectedCountry === 'US') {
    additionalContactOptions = isAnnual
      ? [
          {name: 'Up To 1,000 Contacts: $346', value: '1000-346'},
          {name: 'Up To 2,500 Contacts: $792', value: '2500-792'},
          {name: 'Up To 5,000 Contacts: $1,440', value: '5000-1440'},
          {name: 'Up To 10,000 Contacts: $2,304', value: '10000-2304'},
          {name: 'Up To 15,000 Contacts: $3,168', value: '15000-3168'},
          {name: 'Up To 25,000 Contacts: $4,032', value: '25000-4032'},
          {name: 'Up To 50,000 Contacts: $4,608', value: '50000-4608'},
          {name: 'Up To 75,000 Contacts: $5,760', value: '75000-5760'},
          {name: 'Up To 100,000 Contacts: $6,912', value: '100000-6912'},
          {name: 'Up To 200,000 Contacts: $11,520', value: '200000-11520'},
          {name: 'Up To 500,000 Contacts: $19,008', value: '500000-19008'},
          {name: 'Up To 750,000 Contacts: $28,224', value: '750000-28224'},
          {name: 'Up To 1,000,000 Contacts: $35,712', value: '1000000-35712'},
          {name: 'Up To 2,000,000 Contacts: $57,024', value: '2000000-57024'},
          {name: 'Up To 3,000,000 Contacts: $69,120', value: '3000000-69120'},
          {name: 'Up To 4,000,000 Contacts: $81,216', value: '4000000-81216'},
          {name: 'Over 4,000,000 Contacts: $97,344', value: '5000000-97344'},
        ]
      : [
          {name: 'Up To 1,000 Contacts: $36', value: '1000-36'},
          {name: 'Up To 2,500 Contacts: $84', value: '2500-84'},
          {name: 'Up To 5,000 Contacts: $150', value: '5000-150'},
          {name: 'Up To 10,000 Contacts: $240', value: '10000-240'},
          {name: 'Up To 15,000 Contacts: $330', value: '15000-330'},
          {name: 'Up To 25,000 Contacts: $420', value: '25000-420'},
          {name: 'Up To 50,000 Contacts: $480', value: '50000-480'},
          {name: 'Up To 75,000 Contacts: $600', value: '75000-600'},
          {name: 'Up To 100,000 Contacts: $720', value: '100000-720'},
          {name: 'Up To 200,000 Contacts: $1,200', value: '200000-1200'},
          {name: 'Up To 500,000 Contacts: $1,980', value: '500000-1980'},
          {name: 'Up To 750,000 Contacts: $2,940', value: '750000-2940'},
          {name: 'Up To 1,000,000 Contacts: $3,720', value: '1000000-3720'},
          {name: 'Up To 2,000,000 Contacts: $5,940', value: '2000000-5940'},
          {name: 'Up To 3,000,000 Contacts: $7,200', value: '3000000-7200'},
          {name: 'Up To 4,000,000 Contacts: $8,460', value: '4000000-8460'},
          {name: 'Over 4,000,000 Contacts: $10,140', value: '5000000-10140'},
        ]
  } else if (selectedCountry === 'CA') {
    additionalContactOptions = isAnnual
      ? [
          {name: 'Up To 1,000 Contacts: $470', value: '1000-470'},
          {name: 'Up To 2,500 Contacts: $1,094', value: '2500-1094'},
          {name: 'Up To 5,000 Contacts: $1,949', value: '5000-1949'},
          {name: 'Up To 10,000 Contacts: $3,120', value: '10000-3120'},
          {name: 'Up To 15,000 Contacts: $4,282', value: '15000-4282'},
          {name: 'Up To 25,000 Contacts: $5,462', value: '25000-5462'},
          {name: 'Up To 50,000 Contacts: $6,230', value: '50000-6230'},
          {name: 'Up To 75,000 Contacts: $7,786', value: '75000-7786'},
          {name: 'Up To 100,000 Contacts: $9,341', value: '100000-9341'},
          {name: 'Up To 200,000 Contacts: $15,571', value: '200000-15571'},
          {name: 'Up To 500,000 Contacts: $25,690', value: '500000-25690'},
          {name: 'Up To 750,000 Contacts: $38,141', value: '750000-38141'},
          {name: 'Up To 1,000,000 Contacts: $48,259', value: '1000000-48259'},
          {name: 'Up To 2,000,000 Contacts: $77,059', value: '2000000-77059'},
          {name: 'Up To 3,000,000 Contacts: $93,408', value: '3000000-93408'},
          {name: 'Up To 4,000,000 Contacts: $109,757', value: '4000000-109757'},
          {name: 'Over 4,000,000 Contacts: $131,549', value: '5000000-131549'},
        ]
      : [
          {name: 'Up To 1,000 Contacts: $49', value: '1000-49'},
          {name: 'Up To 2,500 Contacts: $114', value: '2500-114'},
          {name: 'Up To 5,000 Contacts: $203', value: '5000-203'},
          {name: 'Up To 10,000 Contacts: $325', value: '10000-325'},
          {name: 'Up To 15,000 Contacts: $446', value: '15000-446'},
          {name: 'Up To 25,000 Contacts: $568', value: '25000-568'},
          {name: 'Up To 50,000 Contacts: $649', value: '50000-649'},
          {name: 'Up To 75,000 Contacts: $811', value: '75000-811'},
          {name: 'Up To 100,000 Contacts: $973', value: '100000-973'},
          {name: 'Up To 200,000 Contacts: $1,622', value: '200000-1622'},
          {name: 'Up To 500,000 Contacts: $2,676', value: '500000-2676'},
          {name: 'Up To 750,000 Contacts: $3,973', value: '750000-3973'},
          {name: 'Up To 1,000,000 Contacts: $5,027', value: '1000000-5027'},
          {name: 'Up To 2,000,000 Contacts: $8,027', value: '2000000-8027'},
          {name: 'Up To 3,000,000 Contacts: $9,730', value: '3000000-9730'},
          {name: 'Up To 4,000,000 Contacts: $11,433', value: '4000000-11433'},
          {name: 'Over 4,000,000 Contacts: $13,703', value: '4000000-13703'},
        ]
  } else if (selectedCountry === 'AU') {
    additionalContactOptions = isAnnual
      ? [
          {name: 'Up To 1,000 Contacts: AU $432.00', value: '1000-432.00'},
          {name: 'Up To 2,500 Contacts: AU $972.00', value: '2500-972.00'},
          {name: 'Up To 5,000 Contacts: AU $1,775.00', value: '5000-1775.00'},
          {
            name: 'Up To 10,000 Contacts: AU $2,833.00',
            value: '10000-2833.00',
          },
          {
            name: 'Up To 15,000 Contacts: AU $3,900.00',
            value: '15000-3900.00',
          },
          {
            name: 'Up To 25,000 Contacts: AU $4,968.00',
            value: '25000-4968.00',
          },
          {
            name: 'Up To 50,000 Contacts: AU $5,676.00',
            value: '50000-5676.00',
          },
          {
            name: 'Up To 75,000 Contacts: AU $7,092.00',
            value: '75000-7092.00',
          },
          {
            name: 'Up To 100,000 Contacts: AU $8,507.00',
            value: '100000-8507.00',
          },
          {
            name: 'Up To 200,000 Contacts: AU $14,185.00',
            value: '200000-14185.00',
          },
          {
            name: 'Up To 500,000 Contacts: AU $23,400.00',
            value: '500000-23400.00',
          },
          {
            name: 'Up To 750,000 Contacts: AU $34,740.00',
            value: '750000-34740.00',
          },
          {
            name: 'Up To 1,000,000 Contacts: AU $43,956.00',
            value: '1000000-43956.00',
          },
          {
            name: 'Up To 2,000,000 Contacts: AU $70,188.00',
            value: '2000000-70188.00',
          },
          {
            name: 'Up To 3,000,000 Contacts: AU $85,067.00',
            value: '3000000-85067.00',
          },
          {
            name: 'Up To 4,000,000 Contacts: AU $99,960.00',
            value: '4000000-99960.00',
          },
          {
            name: 'Over 4,000,000 Contacts: AU $119,808.00',
            value: '5000000-119808.00',
          },
        ]
      : [
          {
            name: 'Up To 1,000 Contacts: AU $42.00',
            value: '1000-42.00',
          },
          {
            name: 'Up To 2,500 Contacts: AU $98.00',
            value: '2500-98.00',
          },
          {
            name: 'Up To 5,000 Contacts: AU $178.00',
            value: '5000-178.00',
          },
          {
            name: 'Up To 10,000 Contacts: AU $282.00',
            value: '10000-282.00',
          },
          {
            name: 'Up To 15,000 Contacts: AU $389.00',
            value: '15000-389.00',
          },
          {
            name: 'Up To 25,000 Contacts: AU $496.00',
            value: '25000-496.00',
          },
          {
            name: 'Up To 50,000 Contacts: AU $567.00',
            value: '50000-567.00',
          },
          {
            name: 'Up To 75,000 Contacts: AU $707.00',
            value: '75000-707.00',
          },
          {
            name: 'Up To 100,000 Contacts: AU $850.00',
            value: '100000-850.00',
          },
          {
            name: 'Up To 200,000 Contacts: AU $1,415.00',
            value: '200000-1415.00',
          },
          {
            name: 'Up To 500,000 Contacts: AU $1,865.00',
            value: '500000-1865.00',
          },
          {
            name: 'Up To 750,000 Contacts: AU $3,458.00',
            value: '750000-3458.00',
          },
          {
            name: 'Up To 1,000,000 Contacts: AU $4,376.00',
            value: '1000000-4376.00',
          },
          {
            name: 'Up To 2,000,000 Contacts: AU $6,989.00',
            value: '2000000-6989.00',
          },
          {
            name: 'Up To 3,000,000 Contacts: AU $8,471.00',
            value: '3000000-8471.00',
          },
          {
            name: 'Up To 4,000,000 Contacts: AU $9,953.00',
            value: '4000000-9953.00',
          },
          {
            name: 'Over 4,000,000 Contacts: AU $11,930.00',
            value: '5000000-11930.00',
          },
        ]
  } else if (selectedCountry === 'NZ') {
    additionalContactOptions = isAnnual
      ? [
          {name: 'Up To 1,000 Contacts: $442', value: '1000-442'},
          {name: 'Up To 2,500 Contacts: $1,037', value: '2500-1037'},
          {name: 'Up To 5,000 Contacts: $1,882', value: '5000-1882'},
          {name: 'Up To 10,000 Contacts: $2,976', value: '10000-2976'},
          {name: 'Up To 15,000 Contacts: $4,109', value: '15000-4109'},
          {name: 'Up To 25,000 Contacts: $5,453', value: '25000-5453'},
          {name: 'Up To 50,000 Contacts: $5,990', value: '50000-5990'},
          {name: 'Up To 75,000 Contacts: $7,469', value: '75000-7469'},
          {
            name: 'Up To 100,000 Contacts: $8,976',
            value: '100000-8976',
          },
          {
            name: 'Up To 200,000 Contacts: $14,947',
            value: '200000-14947',
          },
          {
            name: 'Up To 500,000 Contacts: $19,699',
            value: '500000-19699',
          },
          {
            name: 'Up To 750,000 Contacts: $36,518',
            value: '750000-36518',
          },
          {
            name: 'Up To 1,000,000 Contacts: $46,214',
            value: '1000000-46214',
          },
          {
            name: 'Up To 2,000,000 Contacts: $73,814',
            value: '2000000-73814',
          },
          {
            name: 'Up To 3,000,000 Contacts: $89,462',
            value: '3000000-89462',
          },
          {
            name: 'Up To 4,000,000 Contacts: $105,110',
            value: '4000000-105110',
          },
          {
            name: 'Over 4,000,000 Contacts: $125,990',
            value: '5000000-125990',
          },
        ]
      : [
          {name: 'Up To 1,000 Contacts: $46.00', value: '1000-46.00'},
          {name: 'Up To 2,500 Contacts: $108.00', value: '2500-108.00'},
          {name: 'Up To 5,000 Contacts: $196.00', value: '5000-196.00'},
          {name: 'Up To 10,000 Contacts: $310.00', value: '10000-310.00'},
          {name: 'Up To 15,000 Contacts: $428.00', value: '15000-428.00'},
          {name: 'Up To 25,000 Contacts: $546.00', value: '25000-546.00'},
          {name: 'Up To 50,000 Contacts: $624.00', value: '50000-624.00'},
          {name: 'Up To 75,000 Contacts: $778.00', value: '75000-778.00'},
          {name: 'Up To 100,000 Contacts: $935.00', value: '100000-935.00'},
          {
            name: 'Up To 200,000 Contacts: $1,557.00',
            value: '200000-1557.00',
          },
          {
            name: 'Up To 500,000 Contacts: $2,052.00',
            value: '500000-2052.00',
          },
          {
            name: 'Up To 750,000 Contacts: $3,804.00',
            value: '750000-3804.00',
          },
          {
            name: 'Up To 1,000,000 Contacts: $4,814.00',
            value: '1000000-4814.00',
          },
          {
            name: 'Up To 2,000,000 Contacts: $7,689.00',
            value: '2000000-7689.00',
          },
          {
            name: 'Up To 3,000,000 Contacts: $9,319.00',
            value: '3000000-9319.00',
          },
          {
            name: 'Up To 4,000,000 Contacts: $10,949.00',
            value: '4000000-10949.00',
          },
          {
            name: 'Over 4,000,000 Contacts: $13,124.00',
            value: '5000000-13124.00',
          },
        ]
  } else if (selectedCountry === 'GB') {
    additionalContactOptions = isAnnual
      ? [
          {name: 'Up To 1,000 Contacts: £278', value: '1000-278'},
          {name: 'Up To 2,500 Contacts: £653', value: '2500-653'},
          {name: 'Up To 5,000 Contacts: £1,171', value: '5000-1171'},
          {name: 'Up To 10,000 Contacts: £1,882', value: '10000-1882'},
          {name: 'Up To 15,000 Contacts: £2,582', value: '15000-2582'},
          {name: 'Up To 25,000 Contacts: £3,283', value: '25000-3283'},
          {name: 'Up To 50,000 Contacts: £3,744', value: '50000-3744'},
          {name: 'Up To 75,000 Contacts: £4,685', value: '75000-4685'},
          {name: 'Up To 100,000 Contacts: £5,626', value: '100000-5626'},
          {name: 'Up To 200,000 Contacts: £9,370', value: '200000-9370'},
          {name: 'Up To 500,000 Contacts: £15,446', value: '500000-15446'},
          {name: 'Up To 750,000 Contacts: £22,944', value: '750000-22944'},
          {name: 'Up To 1,000,000 Contacts: £29,030', value: '1000000-29030'},
          {name: 'Up To 2,000,000 Contacts: £46,358', value: '2000000-46358'},
          {name: 'Up To 3,000,000 Contacts: £56,198', value: '3000000-56198'},
          {name: 'Up To 4,000,000 Contacts: £66,029', value: '4000000-66029'},
          {name: 'Over 4,000,000 Contacts: £79,142', value: '5000000-79142'},
        ]
      : [
          {name: 'Up To 1,000 Contacts: £29.00', value: '1000-29.00'},
          {name: 'Up To 2,500 Contacts: £68.00', value: '2500-68.00'},
          {name: 'Up To 5,000 Contacts: £122.00', value: '5000-122.00'},
          {name: 'Up To 10,000 Contacts: £196.00', value: '10000-196.00'},
          {name: 'Up To 15,000 Contacts: £269.00', value: '15000-269.00'},
          {name: 'Up To 25,000 Contacts: £342.00', value: '25000-342.00'},
          {name: 'Up To 50,000 Contacts: £390.00', value: '50000-390.00'},
          {name: 'Up To 75,000 Contacts: £488.00', value: '75000-488.00'},
          {name: 'Up To 100,000 Contacts: £586.00', value: '100000-586.00'},
          {name: 'Up To 200,000 Contacts: £976.00', value: '200000-976.00'},
          {name: 'Up To 500,000 Contacts: £1,609.00', value: '500000-1609.00'},
          {name: 'Up To 750,000 Contacts: £2,390.00', value: '750000-2390.00'},
          {
            name: 'Up To 1,000,000 Contacts: £3,024.00',
            value: '1000000-3024.00',
          },
          {
            name: 'Up To 2,000,000 Contacts: £4,829.00',
            value: '2000000-4829.00',
          },
          {
            name: 'Up To 3,000,000 Contacts: £5,854.00',
            value: '3000000-5854.00',
          },
          {
            name: 'Up To 4,000,000 Contacts: £6,878.00',
            value: '4000000-6878.00',
          },
          {
            name: 'Over 4,000,000 Contacts: £8,244.00',
            value: '5000000-8244.00',
          },
        ]
  }

  return additionalContactOptions
}

export const HIDE_TIER1_PLAN_CODES = [
  'keap_max-5-thryv-m2m',
  'keap_max-5-thryv-part-m2m',
  'keap_ultimate-5-thryv-m2m',
  'keap_ultimate-5-thryv-part-m2m',
  'keap_max-5-thryv-infl-m2m',
  'keap_max-5-thryv-annual',
  'keap_max-5-thryv-part-annual',
  'keap_ultimate-5-thryv-annual',
  'keap_ultimate-5-thryv-part-annual',
  'keap_max-infl-5-thryv-annual',
]

export const getKeapTextVoiceOptionsByCountry = (
  selectedCountry = 'US',
  billingType = 'Monthly',
  hideTierOne = false,
) => {
  const HIDE_KEAP_TEXT_VOICE_TIER_1 = hideTierOne
  const isAnnual = billingType?.includes('Annual')
  let textVoiceOptions = []
  if (selectedCountry === 'US') {
    textVoiceOptions = isAnnual
      ? [
          {name: 'Tier 1: $168', value: '1-168'},
          {name: 'Tier 2: $288', value: '2-288'},
          {name: 'Tier 3: $468', value: '3-468'},
          {name: 'Tier 4: $948', value: '4-948'},
          {name: 'Tier 5: $1,668', value: '5-1668'},
          {name: 'Tier 6: $3,348', value: '6-3348'},
        ]
      : [
          {name: 'Tier 1: $14', value: '1-14'},
          {name: 'Tier 2: $24', value: '2-24'},
          {name: 'Tier 3: $39', value: '3-39'},
          {name: 'Tier 4: $79', value: '4-79'},
          {name: 'Tier 5: $139', value: '5-139'},
          {name: 'Tier 6: $279', value: '6-279'},
        ]
  } else if (selectedCountry === 'CA') {
    textVoiceOptions = isAnnual
      ? [
          {name: 'Tier 1: $228', value: '1-228'},
          {name: 'Tier 2: $384', value: '2-384'},
          {name: 'Tier 3: $636', value: '3-636'},
          {name: 'Tier 4: $1,284', value: '4-1284'},
          {name: 'Tier 5: $2,256', value: '5-2256'},
          {name: 'Tier 6: $4,524', value: '6-4524'},
        ]
      : [
          {name: 'Tier 1: $19', value: '1-19'},
          {name: 'Tier 2: $33', value: '2-33'},
          {name: 'Tier 3: $53', value: '3-53'},
          {name: 'Tier 4: $107', value: '4-107'},
          {name: 'Tier 5: $188', value: '5-188'},
          {name: 'Tier 6: $377', value: '6-377'},
        ]
  } else if (selectedCountry === 'AU') {
    textVoiceOptions = isAnnual
      ? [
          {name: 'Tier 1: AU $252.00', value: '1-252.00'},
          {name: 'Tier 2: AU $480.00', value: '2-480.00'},
          {name: 'Tier 3: AU $780.00', value: '3-780.00'},
          {name: 'Tier 4: AU $1,560.00', value: '4-1560.00'},
          {name: 'Tier 5: AU $2,760.00', value: '5-2760.00'},
          {name: 'Tier 6: AU $5,580.00', value: '6-5580.00'},
        ]
      : [
          {name: 'Tier 1: AU $21.00', value: '1-21.00'},
          {name: 'Tier 2: AU $40.00', value: '2-40.00'},
          {name: 'Tier 3: AU $65.00', value: '3-65.00'},
          {name: 'Tier 4: AU $130.00', value: '4-130.00'},
          {name: 'Tier 5: AU $230.00', value: '5-230.00'},
          {name: 'Tier 6: AU $465.00', value: '6-465.00'},
        ]
    // } else if (selectedCountry === 'NZ') {
    //   textVoiceOptions = [
    //     {name: 'Tier 1: $19', value: '1-19'},
    //     {name: 'Tier 2: $33', value: '2-33'},
    //     {name: 'Tier 3: $53', value: '3-53'},
    //     {name: 'Tier 4: $107', value: '4-107'},
    //     {name: 'Tier 5: $188', value: '5-188'},
    //     {name: 'Tier 6: $377', value: '6-377'},
    //   ]
  } else if (selectedCountry === 'GB') {
    textVoiceOptions = isAnnual
      ? [
          {name: 'Tier 1: £132', value: '1-132'},
          {name: 'Tier 2: £228', value: '2-228'},
          {name: 'Tier 3: £372', value: '3-372'},
          {name: 'Tier 4: £744', value: '4-744'},
          {name: 'Tier 5: £1,296', value: '5-1296'},
          {name: 'Tier 6: £2,628', value: '6-2628'},
        ]
      : [
          {name: 'Tier 1: £11', value: '1-11'},
          {name: 'Tier 2: £19', value: '2-19'},
          {name: 'Tier 3: £31', value: '3-31'},
          {name: 'Tier 4: £62', value: '4-62'},
          {name: 'Tier 5: £108', value: '5-108'},
          {name: 'Tier 6: £219', value: '6-219'},
        ]
  }

  if (HIDE_KEAP_TEXT_VOICE_TIER_1) {
    textVoiceOptions = textVoiceOptions.filter(
      option => Number(option?.value?.split('-')?.[0]) >= 2,
    )
  }

  return textVoiceOptions
}

export const getKeapLegacyGraphlyContactsByCountry = (
  selectedCountry = 'US',
  billingType = 'Monthly',
) => {
  const isAnnual = billingType?.includes('Annual')
  if (isAnnual) {
    if (selectedCountry === 'US') {
      return [
        {name: 'Up To 1,000 Contacts: $102', value: '1000-102'},
        {name: 'Up To 2,500 Contacts: $153', value: '2500-153'},
        {name: 'Up To 5,000 Contacts: $204', value: '5000-204'},
        {name: 'Up To 10,000 Contacts: $306', value: '10000-306'},
        {name: 'Up To 25,000 Contacts: $510', value: '25000-510'},
        {name: 'Up To 50,000 Contacts: $816', value: '50000-816'},
        {name: 'Up To 75,000 Contacts: $1,020', value: '75000-1020'},
        {name: 'Up To 100,000 Contacts: $1,224', value: '100000-1224'},
        {name: 'Up To 200,000 Contacts: $1,663.62', value: '200000-1663.62'},
        {name: 'Up To 500,000 Contacts: $3,570', value: '500000-3570'},
        {name: 'Up To 750,000 Contacts: $5,100', value: '750000-5100'},
        {name: 'Up To 1,000,000 Contacts: $7,140', value: '1000000-7140'},
        {name: 'Up To 2,000,000 Contacts: $9,690', value: '2000000-9690'},
        {name: 'Up To 3,000,000 Contacts: $11,730', value: '3000000-11730'},
        {name: 'Up To 4,000,000 Contacts: $13,260', value: '4000000-13260'},
        {name: 'Over 4,000,000 Contacts: $14,280', value: '4000000-14280'},
      ]
    } else if (selectedCountry === 'CA') {
      return [
        {name: 'Up To 1,000 Contacts: $142.80', value: '1000-142.80'},
        {name: 'Up To 2,500 Contacts: $204', value: '2500-204'},
        {name: 'Up To 5,000 Contacts: $285.50', value: '5000-285.60'},
        {name: 'Up To 10,000 Contacts: $418.20', value: '10000-418.20'},
        {name: 'Up To 25,000 Contacts: $714', value: '25000-714'},
        {name: 'Up To 50,000 Contacts: $1,122', value: '50000-1122'},
        {name: 'Up To 75,000 Contacts: $1,407', value: '75000-1407'},
        {name: 'Up To 100,000 Contacts: $1,693.20', value: '100000-1693.20'},
        {name: 'Up To 200,000 Contacts: $2,805', value: '200000-2805'},
        {name: 'Up To 500,000 Contacts: $4,926.40', value: '500000-4926.60'},
        {name: 'Up To 750,000 Contacts: $7,038', value: '750000-7038'},
        {name: 'Up To 1,000,000 Contacts: $9,863.40', value: '1000000-9863.40'},
        {
          name: 'Up To 2,000,000 Contacts: $13,382.40',
          value: '2000000-13382.40',
        },
        {
          name: 'Up To 3,000,000 Contacts: $16,197.60',
          value: '3000000-16197.60',
        },
        {name: 'Up To 4,000,000 Contacts: $18,309', value: '4000000-18309'},
        {name: 'Over 4,000,000 Contacts: $19,716', value: '4000000-19716'},
      ]
    } else if (selectedCountry === 'AU') {
      return [
        {name: 'Up To 1,000 Contacts: $165.30', value: '1000-165.30'},
        {name: 'Up To 2,500 Contacts: $247.95', value: '2500-247.95'},
        {name: 'Up To 5,000 Contacts: $330.61', value: '5000-330.61'},
        {name: 'Up To 10,000 Contacts: $495.90', value: '10000-495.90'},
        {name: 'Up To 25,000 Contacts: $826.52', value: '25000-826.52'},
        {name: 'Up To 50,000 Contacts: $1,322.43', value: '50000-1322.43'},
        {name: 'Up To 75,000 Contacts: $1,653.04', value: '75000-1653.04'},
        {name: 'Up To 100,000 Contacts: $1,983.64', value: '100000-1983.64'},
        {name: 'Up To 200,000 Contacts: $3,306.08', value: '200000-3306.08'},
        {name: 'Up To 500,000 Contacts: $5,785.65', value: '500000-5785.65'},
        {name: 'Up To 750,000 Contacts: $8,265.21', value: '750000-8265.21'},
        {
          name: 'Up To 1,000,000 Contacts: $11,571.30',
          value: '1000000-11571.30',
        },
        {
          name: 'Up To 2,000,000 Contacts: $15,703.90',
          value: '2000000-15703.90',
        },
        {
          name: 'Up To 3,000,000 Contacts: $19,009.98',
          value: '3000000-19009.98',
        },
        {
          name: 'Up To 4,000,000 Contacts: $21,489.55',
          value: '4000000-21489.55',
        },
        {
          name: 'Over 4,000,000 Contacts: $23,142.59',
          value: '4000000-23142.59',
        },
      ]
    } else if (selectedCountry === 'NZ') {
      return [
        {name: 'Up To 1,000 Contacts: $165.27', value: '1000-165.27'},
        {name: 'Up To 2,500 Contacts: $247.85', value: '2500-247.85'},
        {name: 'Up To 5,000 Contacts: $330.54', value: '5000-330.54'},
        {name: 'Up To 10,000 Contacts: $495.81', value: '10000-495.81'},
        {name: 'Up To 25,000 Contacts: $826.47', value: '25000-826.47'},
        {name: 'Up To 50,000 Contacts: $1,322.39', value: '50000-1322.39'},
        {name: 'Up To 75,000 Contacts: $1,653.04', value: '75000-1653.04'},
        {name: 'Up To 100,000 Contacts: $1,983.58', value: '100000-1983.58'},
        {name: 'Up To 200,000 Contacts: $3,306.09', value: '200000-3306.09'},
        {name: 'Up To 500,000 Contacts: $5,785.59', value: '500000-5785.59'},
        {name: 'Up To 750,000 Contacts: $8,265.21', value: '750000-8265.21'},
        {
          name: 'Up To 1,000,000 Contacts: $11,571.30',
          value: '1000000-11571.30',
        },
        {
          name: 'Up To 2,000,000 Contacts: $15,703.85',
          value: '2000000-15703.85',
        },
        {
          name: 'Up To 3,000,000 Contacts: $19,009.93',
          value: '3000000-19009.93',
        },
        {
          name: 'Up To 4,000,000 Contacts: $21,489.55',
          value: '4000000-21489.55',
        },
        {
          name: 'Over 4,000,000 Contacts: $23,142.82',
          value: '4000000-23142.82',
        },
      ]
    } else {
      return [
        {name: 'Up To 1,000 Contacts: £83.18', value: '1000-83.18'},
        {name: 'Up To 2,500 Contacts: £124.77', value: '2500-124.77'},
        {name: 'Up To 5,000 Contacts: £166.36', value: '5000-166.36'},
        {name: 'Up To 10,000 Contacts: £249.54', value: '10000-249.54'},
        {name: 'Up To 25,000 Contacts: £415.90', value: '25000-415.90'},
        {name: 'Up To 50,000 Contacts: £665.44', value: '50000-665.44'},
        {name: 'Up To 75,000 Contacts: £831.81', value: '75000-831.81'},
        {name: 'Up To 100,000 Contacts: £998.17', value: '100000-998.17'},
        {name: 'Up To 200,000 Contacts: £2,040', value: '200000-2040'},
        {name: 'Up To 500,000 Contacts: £2,911.33', value: '500000-2911.33'},
        {name: 'Up To 750,000 Contacts: £4,159.05', value: '750000-4159.05'},
        {name: 'Up To 1,000,000 Contacts: £5,822.67', value: '1000000-5822.67'},
        {name: 'Up To 2,000,000 Contacts: £7,902.19', value: '2000000-7902.19'},
        {name: 'Up To 3,000,000 Contacts: £9,566.81', value: '3000000-9566.81'},
        {
          name: 'Up To 4,000,000 Contacts: £10,813.53',
          value: '4000000-10813.53',
        },
        {
          name: 'Over 4,000,000 Contacts: £11,645.34',
          value: '4000000-11645.34',
        },
      ]
    }
  } else {
    if (selectedCountry === 'US') {
      return [
        {name: 'Up To 1,000 Contacts: $10', value: '1000-10'},
        {name: 'Up To 2,500 Contacts: $15', value: '2500-15'},
        {name: 'Up To 5,000 Contacts: $20', value: '5000-20'},
        {name: 'Up To 10,000 Contacts: $30', value: '10000-30'},
        {name: 'Up To 25,000 Contacts: $50', value: '25000-50'},
        {name: 'Up To 50,000 Contacts: $80', value: '50000-80'},
        {name: 'Up To 75,000 Contacts: $100', value: '75000-100'},
        {name: 'Up To 100,000 Contacts: $120', value: '100000-120'},
        {name: 'Up To 200,000 Contacts: $200', value: '200000-200'},
        {name: 'Up To 500,000 Contacts: $350', value: '500000-350'},
        {name: 'Up To 750,000 Contacts: $500', value: '750000-500'},
        {name: 'Up To 1,000,000 Contacts: $700', value: '1000000-700'},
        {name: 'Up To 2,000,000 Contacts: $950', value: '2000000-950'},
        {name: 'Up To 3,000,000 Contacts: $1,150', value: '3000000-1150'},
        {name: 'Up To 4,000,000 Contacts: $1,300', value: '4000000-1300'},
        {name: 'Over 4,000,000 Contacts: $1,400', value: '4000000-1400'},
      ]
    } else if (selectedCountry === 'CA') {
      return [
        {name: 'Up To 1,000 Contacts: $14', value: '1000-14'},
        {name: 'Up To 2,500 Contacts: $20', value: '2500-20'},
        {name: 'Up To 5,000 Contacts: $28', value: '5000-28'},
        {name: 'Up To 10,000 Contacts: $41', value: '10000-41'},
        {name: 'Up To 25,000 Contacts: $70', value: '25000-70'},
        {name: 'Up To 50,000 Contacts: $110', value: '50000-110'},
        {name: 'Up To 75,000 Contacts: $138', value: '75000-138'},
        {name: 'Up To 100,000 Contacts: $166', value: '100000-166'},
        {name: 'Up To 200,000 Contacts: $275', value: '200000-275'},
        {name: 'Up To 500,000 Contacts: $483', value: '500000-483'},
        {name: 'Up To 750,000 Contacts: $690', value: '750000-690'},
        {name: 'Up To 1,000,000 Contacts: $967', value: '1000000-967'},
        {name: 'Up To 2,000,000 Contacts: $1,312', value: '2000000-1312'},
        {name: 'Up To 3,000,000 Contacts: $1,588', value: '3000000-1588'},
        {name: 'Up To 4,000,000 Contacts: $1,795', value: '4000000-1795'},
        {name: 'Over 4,000,000 Contacts: $1,933', value: '4000000-1933'},
      ]
    } else if (selectedCountry === 'AU') {
      return [
        {name: 'Up To 1,000 Contacts: $16.20', value: '1000-16.20'},
        {name: 'Up To 2,500 Contacts: $24.30', value: '2500-24.30'},
        {name: 'Up To 5,000 Contacts: $32.41', value: '5000-32.41'},
        {name: 'Up To 10,000 Contacts: $48.61', value: '10000-48.61'},
        {name: 'Up To 25,000 Contacts: $81.03', value: '25000-81.03'},
        {name: 'Up To 50,000 Contacts: $129.65', value: '50000-129.65'},
        {name: 'Up To 75,000 Contacts: $162.06', value: '75000-162.06'},
        {name: 'Up To 100,000 Contacts: $194.47', value: '100000-194.47'},
        {name: 'Up To 200,000 Contacts: $324.13', value: '200000-324.13'},
        {name: 'Up To 500,000 Contacts: $567.21', value: '500000-567.21'},
        {name: 'Up To 750,000 Contacts: $810.32', value: '750000-810.32'},
        {name: 'Up To 1,000,000 Contacts: $1,134.44', value: '1000000-1134.44'},
        {name: 'Up To 2,000,000 Contacts: $1,539.59', value: '2000000-1539.59'},
        {name: 'Up To 3,000,000 Contacts: $1,863.72', value: '3000000-1863.72'},
        {name: 'Up To 4,000,000 Contacts: $2,106.82', value: '4000000-2106.82'},
        {name: 'Over 4,000,000 Contacts: $2,269.98', value: '4000000-2269.98'},
      ]
    } else if (selectedCountry === 'NZ') {
      return [
        {name: 'Up To 1,000 Contacts: $16.20', value: '1000-16.20'},
        {name: 'Up To 2,500 Contacts: $24.30', value: '2500-24.30'},
        {name: 'Up To 5,000 Contacts: $32.41', value: '5000-32.41'},
        {name: 'Up To 10,000 Contacts: $48.61', value: '10000-48.61'},
        {name: 'Up To 25,000 Contacts: $81.03', value: '25000-81.03'},
        {name: 'Up To 50,000 Contacts: $129.65', value: '50000-129.65'},
        {name: 'Up To 75,000 Contacts: $162.06', value: '75000-162.06'},
        {name: 'Up To 100,000 Contacts: $194.47', value: '100000-194.47'},
        {name: 'Up To 200,000 Contacts: $324.13', value: '200000-324.13'},
        {name: 'Up To 500,000 Contacts: $567.22', value: '500000-567.22'},
        {name: 'Up To 750,000 Contacts: $810.32', value: '750000-810.32'},
        {name: 'Up To 1,000,000 Contacts: $1,134.44', value: '1000000-1134.44'},
        {name: 'Up To 2,000,000 Contacts: $1,539.59', value: '2000000-1539.59'},
        {name: 'Up To 3,000,000 Contacts: $1,863.72', value: '3000000-1863.72'},
        {name: 'Up To 4,000,000 Contacts: $2,106.82', value: '4000000-2106.82'},
        {name: 'Over 4,000,000 Contacts: $2,269.98', value: '4000000-2269.98'},
      ]
    } else {
      return [
        {name: 'Up To 1,000 Contacts: £8.15', value: '1000-8.15'},
        {name: 'Up To 2,500 Contacts: £12.23', value: '2500-12.23'},
        {name: 'Up To 5,000 Contacts: £16.31', value: '5000-16.31'},
        {name: 'Up To 10,000 Contacts: £24.46', value: '10000-24.46'},
        {name: 'Up To 25,000 Contacts: £40.77', value: '25000-40.77'},
        {name: 'Up To 50,000 Contacts: £65.24', value: '50000-65.24'},
        {name: 'Up To 75,000 Contacts: £81.55', value: '75000-81.55'},
        {name: 'Up To 100,000 Contacts: £97.86', value: '100000-97.86'},
        {name: 'Up To 200,000 Contacts: £163.10', value: '200000-163.10'},
        {name: 'Up To 500,000 Contacts: £285.42', value: '500000-285.42'},
        {name: 'Up To 750,000 Contacts: £407.75', value: '750000-407.75'},
        {name: 'Up To 1,000,000 Contacts: £570.85', value: '1000000-570.85'},
        {name: 'Up To 2,000,000 Contacts: £774.72', value: '2000000-774.72'},
        {name: 'Up To 3,000,000 Contacts: £937.82', value: '3000000-937.82'},
        {name: 'Up To 4,000,000 Contacts: £1,060.15', value: '4000000-1060.15'},
        {name: 'Over 4,000,000 Contacts: £1,141.70', value: '4000000-1141.70'},
      ]
    }
  }
}

export const getKeapLegacyPlusThisPremiumByCountry = (
  selectedCountry = 'US',
  billingType = 'Monthly',
) => {
  const isAnnual = billingType?.includes('Annual')
  if (isAnnual) {
    if (selectedCountry === 'US') {
      return [
        {name: 'Up To 200,000 Transactions: $2,388', value: '200000-2388'},
        {name: 'Up To 500,000 Transactions: $3,588', value: '500000-3588'},
        {name: 'Up To 1,000,000 Transactions: $5,988', value: '1000000-5988'},
        {name: 'Up To 2,000,000 Transactions: $8,388', value: '2000000-8388'},
      ]
    } else if (selectedCountry === 'CA') {
      return [
        {name: 'Up To 200,000 Transactions: $3,228', value: '200000-3288'},
        {name: 'Up To 500,000 Transactions: $4,860', value: '500000-4860'},
        {name: 'Up To 1,000,000 Transactions: $8,400', value: '1000000-8400'},
        {name: 'Up To 2,000,000 Transactions: $11,580', value: '2000000-11580'},
      ]
    } else if (selectedCountry === 'AU') {
      return [
        {name: 'Up To 200,000 Transactions: $3,876', value: '200000-3876'},
        {name: 'Up To 500,000 Transactions: $5,016', value: '500000-5016'},
        {name: 'Up To 1,000,000 Transactions: $8,400', value: '1000000-8400'},
        {name: 'Up To 2,000,000 Transactions: $11,760', value: '2000000-11760'},
      ]
    } else if (selectedCountry === 'NZ') {
      return [
        {name: 'Up To 200,000 Transactions: $3,894', value: '200000-3894'},
        {name: 'Up To 500,000 Transactions: $5,544', value: '500000-5544'},
        {name: 'Up To 1,000,000 Transactions: $9,240', value: '1000000-9240'},
        {name: 'Up To 2,000,000 Transactions: $12,936', value: '2000000-12936'},
      ]
    } else {
      return [
        {name: 'Up To 200,000 Transactions: £1,620', value: '200000-1620'},
        {name: 'Up To 500,000 Transactions: £2,676', value: '500000-2676'},
        {name: 'Up To 1,000,000 Transactions: £4,464', value: '1000000-4464'},
        {name: 'Up To 2,000,000 Transactions: £6,240', value: '2000000-6240'},
      ]
    }
  } else {
    if (selectedCountry === 'US') {
      return [
        {name: 'Up To 200,000 Transactions: $199', value: '200000-199'},
        {name: 'Up To 500,000 Transactions: $299', value: '500000-299'},
        {name: 'Up To 1,000,000 Transactions: $499', value: '1000000-499'},
        {name: 'Up To 2,000,000 Transactions: $699', value: '2000000-699'},
      ]
    } else if (selectedCountry === 'CA') {
      return [
        {name: 'Up To 200,000 Transactions: $269', value: '200000-269'},
        {name: 'Up To 500,000 Transactions: $405', value: '500000-405'},
        {name: 'Up To 1,000,000 Transactions: $700', value: '1000000-700'},
        {name: 'Up To 2,000,000 Transactions: $965', value: '2000000-965'},
      ]
    } else if (selectedCountry === 'AU') {
      return [
        {name: 'Up To 200,000 Transactions: $323', value: '200000-323'},
        {name: 'Up To 500,000 Transactions: $418', value: '500000-418'},
        {name: 'Up To 1,000,000 Transactions: $700', value: '1000000-700'},
        {name: 'Up To 2,000,000 Transactions: $980', value: '2000000-980'},
      ]
    } else if (selectedCountry === 'NZ') {
      return [
        {name: 'Up To 200,000 Transactions: $332', value: '200000-332'},
        {name: 'Up To 500,000 Transactions: $462', value: '500000-462'},
        {name: 'Up To 1,000,000 Transactions: $770', value: '1000000-770'},
        {name: 'Up To 2,000,000 Transactions: $1,078', value: '2000000-1078'},
      ]
    } else {
      return [
        {name: 'Up To 200,000 Transactions: £135', value: '200000-135'},
        {name: 'Up To 500,000 Transactions: £223', value: '500000-223'},
        {name: 'Up To 1,000,000 Transactions: £372', value: '1000000-372'},
        {name: 'Up To 2,000,000 Transactions: £520', value: '2000000-520'},
      ]
    }
  }
}

export const getKeapKcpCertificationOptionsByCountry = (
  selectedCountry = 'US',
) => {
  let KcpOptions = []
  if (selectedCountry === 'US') {
    KcpOptions = [
      {name: '$299', value: '1-299'},
      {name: '$499', value: '2-499'},
      {name: '$750', value: '3-750'},
      {name: '$1500', value: '4-1500'},
    ]
  } else if (selectedCountry === 'CA') {
    KcpOptions = [
      {name: '$405', value: '1-405'},
      {name: '$700', value: '2-700'},
      {name: '$1000', value: '3-1000'},
      {name: '$2000', value: '4-2000'},
    ]
  } else if (selectedCountry === 'AU') {
    KcpOptions = [
      {name: '$420', value: '1-420'},
      {name: '$700', value: '2-700'},
      {name: '$1000', value: '3-1000'},
      {name: '$2000', value: '4-2000'},
    ]
  } else if (selectedCountry === 'NZ') {
    KcpOptions = [
      {name: '$462', value: '1-462'},
      {name: '$770', value: '2-770'},
      {name: '$1100', value: '3-1100'},
      {name: '$2200', value: '4-2200'},
    ]
  }

  return KcpOptions
}
